import { getInitialPartySize, DEFAULT_PARTY_SIZE, getStartDate } from '@/store/reducers/getInitialReservationFormStateState'
import type { DiningWidgetInfoResponse } from '@sevenrooms/api'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { DateOnly } from '@sevenrooms/timepiece'
import { createRequiredContext } from '@sevenrooms/ui-kit/hooks'
import { Root } from '@sevenrooms/ui-kit/layout'
import { ErrorPage, FleeTheVoid } from './components/ErrorPage'
import { WidgetContainerSkeleton } from './components/WidgetContainer/WidgetContainerSkeleton'
import { useReservationsRoute } from './hooks/useReservationsRoute'
import { useGetWidgetInfoQuery, useGetSingleDayAvailabilityQuery, useGetLanguageStringsQuery } from './store'
import { useTrackers, usePreloadImage, usePaymentsRedirect } from './utils'

const [VenueDataContextProvider, useVenueDataContext] = createRequiredContext<DiningWidgetInfoResponse>()

function getAvailabilityParams() {
  const partySize = getInitialPartySize({ minGuests: DEFAULT_PARTY_SIZE, maxGuests: 50 })
  const currentDate = new Date()
  const startDateIso = getStartDate({ currentDateAtVenue: currentDate, maxDaysOut: 90 })
  const startDate = DateOnly.fromSafe(startDateIso)?.toIso()
  if (!startDate || currentDate.toISOString() === startDateIso) {
    return { partySize }
  }
  return {
    partySize,
    startDate,
  }
}

const initialAvailabilityParams = getAvailabilityParams()

export function VenueDataLoader({ children }: { children: React.ReactNode }) {
  const { venueKey, isSearchRoute, clientToken, channel } = useReservationsRoute()
  const {
    isLoading: isWidgetInfoLoading,
    data: widgetInfoData,
    isError: isWidgetInfoError,
    isSuccess: isWidgetInfoSuccess,
    error: widgetInfoError,
  } = useGetWidgetInfoQuery({ venue_url_key: venueKey ?? '', client_token: clientToken }, { skip: !venueKey })
  usePaymentsRedirect(widgetInfoData?.venueInfo.paymentType === 'FREEDOMPAY')
  usePreloadImage(widgetInfoData?.widgetSettings?.headerImgMobileUrl)
  // This is a performance optimization designed to catch the most common use cases
  //   This call will pre-fetch availability and cache for the widget using the venue defaults and a few common query params.
  //   To add more query params, update the getAvailabilityParams function
  useGetSingleDayAvailabilityQuery(
    {
      venue: venueKey ?? '',
      channel: channel ?? ReservationWidget.SevenRoomsWidgetChannel,
      haloSizeInterval: ReservationWidget.WholeDayHaloSizeInterval,
      excludePdr: true,
      ...initialAvailabilityParams,
    },
    { skip: !venueKey || isWidgetInfoSuccess }
  )
  useGetLanguageStringsQuery({ venueUrlKey: venueKey ?? '', widgetType: 'res_widget' }, { skip: !venueKey || isWidgetInfoSuccess })
  useTrackers({
    params: {
      adobeId: widgetInfoData?.analyticsSettings.adobeId,
      fbPixelIdsAll: widgetInfoData?.analyticsSettings.fbPixelIdsAll,
      googleTagId: widgetInfoData?.analyticsSettings.googleTagId,
      googleTagManagerId: widgetInfoData?.analyticsSettings.googleTagsId,
      oneTrustId: widgetInfoData?.analyticsSettings.oneTrustId,
      segmentId: widgetInfoData?.analyticsSettings.segmentId,
      srGoogleTagsId: widgetInfoData?.analyticsSettings.srGoogleTagsId,
      suppressSrDiningAnalytics: widgetInfoData?.analyticsSettings.suppressSrDiningAnalytics,
      tikTokId: widgetInfoData?.analyticsSettings.tiktokId,
      twitterId: widgetInfoData?.analyticsSettings.twitterId,
      adrollAdvId: widgetInfoData?.analyticsSettings.adrollAdvId,
      adrollPixId: widgetInfoData?.analyticsSettings.adrollPixId,
      tealiumAccount: widgetInfoData?.analyticsSettings.tealiumAccount,
      tealiumProfile: widgetInfoData?.analyticsSettings.tealiumProfile,
    },
    ready: isWidgetInfoSuccess,
  })

  if (!venueKey) {
    return (
      <Root theme="gx">
        <FleeTheVoid />
      </Root>
    )
  }

  if (isWidgetInfoError) {
    return (
      <Root theme="gx">
        <ErrorPage error={widgetInfoError} />
      </Root>
    )
  }

  if (isWidgetInfoLoading || !widgetInfoData) {
    // Return Loader
    return (
      <Root theme="gx">
        <WidgetContainerSkeleton showGutters={isSearchRoute} />
      </Root>
    )
  }

  return <VenueDataContextProvider value={widgetInfoData}>{children}</VenueDataContextProvider>
}

export { useVenueDataContext }
